/**
 * Componentes del module kind `resource` (F2-A): editor de autoría (kind
 * file|link|text + campos) y visor de consumo. Local-first: leen/escriben la
 * fila `resource` sincronizada; el write-path aplica authz de autor/editor.
 */
import { type Component, createMemo, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ExternalLink, FileText, Link2, Paperclip } from "lucide-solid";
import { updateResourceMutation } from "@space/shared";
import type { ResourceRow } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useCourseResources } from "../hooks/use-course-modules";
import type { ModuleComponentProps } from "./registry";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

function useResourceRow(props: ModuleComponentProps) {
  const resources = useCourseResources(() => props.courseId);
  const row = createMemo<ResourceRow | undefined>(() =>
    (resources.data ?? []).find((r) => r.id === props.refId),
  );
  return row;
}

/** Editor de autoría del recurso (embebido en el course-editor). */
export const ResourceModuleEditor: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const row = useResourceRow(props);

  const save = (patch: Parameters<typeof updateResourceMutation>[2]) => {
    const db = drizzle();
    const r = row();
    if (db && r) {
      void updateResourceMutation(db, r.id, patch);
    }
  };

  return (
    <Show when={row()} fallback={<p class="text-xs text-muted-foreground">…</p>}>
      {(r) => (
        <div class="space-y-2 rounded-lg bg-muted/30 p-3" data-testid="resource-editor">
          <input
            class={field}
            data-testid="edit-resource-title"
            value={r().title ?? ""}
            onChange={(e) => save({ title: e.currentTarget.value })}
            placeholder={t(msg`Resource title`)}
          />
          <select
            class={field}
            data-testid="edit-resource-kind"
            value={r().kind ?? "link"}
            onChange={(e) => {
              const v = e.currentTarget.value;
              if (v === "file" || v === "link" || v === "text") {
                save({ kind: v });
              }
            }}
          >
            <option value="link">{t(msg`Link`)}</option>
            <option value="text">{t(msg`Text`)}</option>
            <option value="file">{t(msg`File`)}</option>
          </select>
          <Show when={(r().kind ?? "link") !== "text"}>
            <input
              class={field}
              data-testid="edit-resource-url"
              value={r().url ?? ""}
              onChange={(e) => save({ url: e.currentTarget.value })}
              placeholder={t(msg`https://…`)}
            />
          </Show>
          <Show when={r().kind === "text"}>
            <textarea
              class={field}
              data-testid="edit-resource-body"
              rows={4}
              value={r().body ?? ""}
              onChange={(e) => save({ body: e.currentTarget.value })}
              placeholder={t(msg`Content for your students`)}
            />
          </Show>
        </div>
      )}
    </Show>
  );
};

/** Visor de consumo del recurso (player del alumno). */
export const ResourceModuleViewer: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  const row = useResourceRow(props);
  return (
    <Show
      when={row()}
      fallback={<p class="text-sm text-muted-foreground">{t(msg`Resource not synced yet.`)}</p>}
    >
      {(r) => (
        <div
          class="space-y-3 rounded-2xl border border-border bg-surface p-4"
          data-testid="resource-viewer"
        >
          <div class="flex items-center gap-2 font-semibold">
            <Show when={r().kind === "text"} fallback={<Link2 class="h-4 w-4 text-accent" />}>
              <FileText class="h-4 w-4 text-accent" />
            </Show>
            <span data-testid="resource-viewer-title">{r().title}</span>
          </div>
          <Show when={r().kind === "text"}>
            <p class="whitespace-pre-wrap text-sm" data-testid="resource-viewer-body">
              {r().body}
            </p>
          </Show>
          <Show when={r().kind !== "text" && r().url}>
            <a
              href={r().url ?? "#"}
              target="_blank"
              rel="noreferrer noopener"
              class="inline-flex items-center gap-1.5 rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground"
              data-testid="resource-viewer-open"
            >
              <ExternalLink class="h-4 w-4" />
              {r().kind === "file" ? t(msg`Download file`) : t(msg`Open link`)}
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
};

/** Fallback genérico para kinds sin componente propio (placeholder F3). */
export const ModuleFallback: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  return (
    <div class="flex items-center gap-2 rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
      <Paperclip class="h-4 w-4" />
      {t(msg`This module type is not available yet.`)} ({props.moduleId})
    </div>
  );
};
